import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";

const DRAFT_PREFIX = "draft:";
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_DEBOUNCE = 500;
const MAX_DRAFT_SIZE = 100 * 1024; // 100KB

// Keys cleared this tick. Prevents useDraftSync from re-saving a draft
// that was explicitly cleared by a parent save handler in the same event.
const _recentlyCleared = new Set();

// --- Storage availability check ---

let _storageOk = false;
try {
  const key = "__draft_test__";
  localStorage.setItem(key, "1");
  localStorage.removeItem(key);
  _storageOk = true;
} catch {
  _storageOk = false;
}

// --- Low-level storage utilities ---

function loadDraftRaw(key) {
  if (!_storageOk) return null;
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveDraftRaw(key, state) {
  if (!_storageOk) return;
  try {
    const data = JSON.stringify({ state, savedAt: Date.now() });
    if (data.length > MAX_DRAFT_SIZE) {
      console.warn(`Draft "${key}" exceeds ${MAX_DRAFT_SIZE / 1024}KB limit, skipping`);
      return;
    }
    localStorage.setItem(DRAFT_PREFIX + key, data);
  } catch {
    // localStorage full or unavailable
  }
}

function removeDraftRaw(key) {
  if (!_storageOk) return;
  try {
    localStorage.removeItem(DRAFT_PREFIX + key);
  } catch {}
}

function isDraftValidCheck(draft, ttlMs, entityUpdatedAt) {
  if (!draft || !draft.savedAt) return false;
  const age = Date.now() - draft.savedAt;
  if (age > ttlMs) return false;
  if (entityUpdatedAt) {
    const updatedMs = new Date(entityUpdatedAt).getTime();
    if (!isNaN(updatedMs) && updatedMs > draft.savedAt) return false;
  }
  return true;
}

function deepEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

// --- Public utilities ---

/**
 * Remove a draft by its full key. Call from parent save handlers
 * to clear the draft after a successful save.
 */
export function clearDraftByKey(key) {
  removeDraftRaw(key);
  _recentlyCleared.add(key);
  setTimeout(() => _recentlyCleared.delete(key), 0);
}

/**
 * Remove expired drafts from localStorage. Call on app startup.
 */
export function cleanupExpiredDrafts(ttlMs = DEFAULT_TTL) {
  if (!_storageOk) return;
  try {
    const now = Date.now();
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(DRAFT_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const draft = JSON.parse(raw);
        if (now - (draft.savedAt || 0) > ttlMs) {
          toRemove.push(key);
        }
      } catch {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}
}

// --- Main hook: replaces useState with draft persistence ---

/**
 * Drop-in replacement for useState that persists form state as a draft
 * in localStorage. Use for dialogs that manage their own state.
 *
 * @param {string|null} draftKey  Storage key, e.g. "option:OPT-00001". Pass null to disable.
 * @param {*} initialState        Default state when no draft exists.
 * @param {object} options
 * @param {number} options.debounceMs  Save debounce delay (default 500).
 * @param {number} options.ttlMs       Draft time-to-live (default 24h).
 * @param {string|null} options.entityUpdatedAt  ISO timestamp; draft is stale if entity was updated after it.
 * @returns {[state, setState, draftMeta]}
 */
export function useDraftState(draftKey, initialState, options = {}) {
  const {
    debounceMs = DEFAULT_DEBOUNCE,
    ttlMs = DEFAULT_TTL,
    entityUpdatedAt = null,
  } = options;

  const initialRef = useRef(initialState);
  const draftKeyRef = useRef(draftKey);
  const skipSaveRef = useRef(false);

  const [state, _setState] = useState(() => {
    if (!draftKey) return initialState;
    const draft = loadDraftRaw(draftKey);
    if (isDraftValidCheck(draft, ttlMs, entityUpdatedAt)) {
      return draft.state;
    }
    if (draft) removeDraftRaw(draftKey);
    return initialState;
  });

  const [hasDraft, setHasDraft] = useState(() => {
    if (!draftKey) return false;
    const draft = loadDraftRaw(draftKey);
    return isDraftValidCheck(draft, ttlMs, entityUpdatedAt);
  });

  const [draftSavedAt, setDraftSavedAt] = useState(() => {
    if (!draftKey) return null;
    const draft = loadDraftRaw(draftKey);
    return isDraftValidCheck(draft, ttlMs, entityUpdatedAt) ? draft.savedAt : null;
  });

  // Handle draftKey changes (e.g. dialog opened for a different entity)
  useEffect(() => {
    if (draftKeyRef.current === draftKey) return;
    draftKeyRef.current = draftKey;
    initialRef.current = initialState;

    if (!draftKey) {
      _setState(initialState);
      setHasDraft(false);
      setDraftSavedAt(null);
      return;
    }

    const draft = loadDraftRaw(draftKey);
    if (isDraftValidCheck(draft, ttlMs, entityUpdatedAt)) {
      skipSaveRef.current = true;
      _setState(draft.state);
      setHasDraft(true);
      setDraftSavedAt(draft.savedAt);
    } else {
      if (draft) removeDraftRaw(draftKey);
      _setState(initialState);
      setHasDraft(false);
      setDraftSavedAt(null);
    }
  }, [draftKey, initialState, ttlMs, entityUpdatedAt]);

  // Debounced save to localStorage
  useEffect(() => {
    if (!draftKey) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    if (deepEqual(state, initialRef.current)) return;

    const timer = setTimeout(() => {
      saveDraftRaw(draftKey, state);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [state, draftKey, debounceMs]);

  const setState = useCallback((updater) => {
    _setState(updater);
  }, []);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      removeDraftRaw(draftKey);
      _recentlyCleared.add(draftKey);
      setTimeout(() => _recentlyCleared.delete(draftKey), 0);
    }
    setHasDraft(false);
    setDraftSavedAt(null);
  }, [draftKey]);

  const discardDraft = useCallback(() => {
    if (draftKey) removeDraftRaw(draftKey);
    _setState(initialRef.current);
    setHasDraft(false);
    setDraftSavedAt(null);
  }, [draftKey]);

  const isDirty = useMemo(
    () => !deepEqual(state, initialRef.current),
    [state]
  );

  const draftAge = draftSavedAt ? Date.now() - draftSavedAt : null;

  return [state, setState, { hasDraft, draftAge, draftSavedAt, discardDraft, clearDraft, isDirty }];
}

// --- Sync hook: persistence for parent-managed state ---

/**
 * Adds draft persistence to state managed outside this hook (e.g. state
 * owned by a parent component). Does NOT manage state itself.
 *
 * @param {string|null} draftKey  Storage key, or null to disable.
 * @param {*} state               Current form state.
 * @param {Function} setState     Setter for the form state.
 * @param {object} options         Same as useDraftState.
 * @returns {draftMeta}
 */
export function useDraftSync(draftKey, state, setState, options = {}) {
  const {
    debounceMs = DEFAULT_DEBOUNCE,
    ttlMs = DEFAULT_TTL,
    entityUpdatedAt = null,
  } = options;

  const stateRef = useRef(state);
  stateRef.current = state;
  const initialRef = useRef(null);
  const prevKeyRef = useRef(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // When key activates/changes: check for draft and restore (before paint)
  useLayoutEffect(() => {
    if (draftKey === prevKeyRef.current) return;

    // Save draft for previous key if dirty and not recently cleared
    if (prevKeyRef.current && initialRef.current !== null) {
      if (
        !_recentlyCleared.has(prevKeyRef.current) &&
        !deepEqual(stateRef.current, initialRef.current)
      ) {
        saveDraftRaw(prevKeyRef.current, stateRef.current);
      }
    }

    prevKeyRef.current = draftKey;

    if (!draftKey) {
      initialRef.current = null;
      setHasDraft(false);
      setDraftSavedAt(null);
      return;
    }

    // Capture the current state as "initial" for this key
    initialRef.current = stateRef.current;

    const draft = loadDraftRaw(draftKey);
    if (isDraftValidCheck(draft, ttlMs, entityUpdatedAt)) {
      setState(draft.state);
      setHasDraft(true);
      setDraftSavedAt(draft.savedAt);
    } else {
      if (draft) removeDraftRaw(draftKey);
      setHasDraft(false);
      setDraftSavedAt(null);
    }
  }, [draftKey, ttlMs, entityUpdatedAt, setState]);

  // Debounced save
  useEffect(() => {
    if (!draftKey) return;
    if (initialRef.current !== null && deepEqual(state, initialRef.current)) return;

    const timer = setTimeout(() => saveDraftRaw(draftKey, state), debounceMs);
    return () => clearTimeout(timer);
  }, [state, draftKey, debounceMs]);

  // Save on unmount (unless recently cleared)
  useEffect(() => {
    return () => {
      if (prevKeyRef.current && initialRef.current !== null) {
        if (
          !_recentlyCleared.has(prevKeyRef.current) &&
          !deepEqual(stateRef.current, initialRef.current)
        ) {
          saveDraftRaw(prevKeyRef.current, stateRef.current);
        }
      }
    };
  }, []);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      removeDraftRaw(draftKey);
      _recentlyCleared.add(draftKey);
      setTimeout(() => _recentlyCleared.delete(draftKey), 0);
    }
    initialRef.current = stateRef.current;
    setHasDraft(false);
    setDraftSavedAt(null);
  }, [draftKey]);

  const discardDraft = useCallback(() => {
    if (draftKey) removeDraftRaw(draftKey);
    if (initialRef.current !== null) setState(initialRef.current);
    setHasDraft(false);
    setDraftSavedAt(null);
  }, [draftKey, setState]);

  const isDirty = useMemo(() => {
    if (initialRef.current === null) return false;
    return !deepEqual(state, initialRef.current);
  }, [state]);

  return {
    hasDraft,
    draftAge: draftSavedAt ? Date.now() - draftSavedAt : null,
    draftSavedAt,
    discardDraft,
    clearDraft,
    isDirty,
  };
}
